/** Design-system palette / theme / density constants.
 *
 * Source of truth: apps/web/styles/tokens.css. The five palettes share the
 * sensitivity hue stack; only --bg / --surface / --fg / --accent vary.
 */

export const PALETTES = [
  // Modern Japanese minimal stack — shipped originally.
  "shu",
  "ai",
  "sumi",
  "cha",
  "kin",
  // Heian kasane no irome (襲の色目) — Genji-era court layered palettes.
  // Each is built around a classic colour pairing the Heian-period
  // reader would have named on sight. See tokens.css for the source
  // chapter / aesthetic note per palette.
  "kobai",
  "murasaki",
  "kuchiba",
  "tsuyukusa",
  // Russian imperial — 19th-century aristocratic-military aesthetic.
  // Tolstoy's drawing rooms, Decembrist-era regimental uniforms,
  // Petersburg winter linens. First non-Japanese palette in the
  // stack; glyph label uses Cyrillic. See tokens.css for the source
  // colour cues per palette.
  "dekabrist",
  // Byzantine imperial — Justinian-era through Komnenian visual
  // canon: Hagia Sophia gold-mosaic walls, Tyrian (porphyra) purple
  // imperial silk, lapis lazuli icons, Constantinople ivory.
  // Glyph label uses Greek (Byzantines wrote in Greek and called
  // themselves Romans). See tokens.css for source cues; also has a
  // companion ``palette-porphyra.css`` for gold-halo decoration.
  "porphyra",
  // 19th-century French Romantic — Stendhal's "Le Rouge et le Noir"
  // (1830). Built around the title's signature tension: wine-crimson
  // (le rouge — passion, blood, the Napoleonic army uniform Julien
  // dreams of) against seminary-black (le noir — the cassock, the
  // Restoration establishment Julien actually climbs). Supported by
  // Restoration gilt (the corrupt salons) + Verrières parchment
  // (provincial drawing rooms) + a hint of forest green (the country
  // estate). Glyph label uses Latin letters: "R&N" captures the
  // title's dichotomy without abbreviation gymnastics.
  "stendhal",
  // Chinese Song dynasty (960–1279) — celadon (青瓷 Ru / Longquan
  // ware) + sumi-ink wash + cinnabar (朱砂) imperial seal +
  // Ding-ware ivory + smoke-gray of Fan Kuan / Guo Xi misty-
  // mountain landscape painting. The most aesthetically refined
  // dynasty in Chinese history; the palette mirrors that
  // restraint. Single-character glyph 宋 (the dynasty's name).
  "song",
  // 诗经 · 蒹葭 — pre-Qin riverbank lyric: reeds "苍苍",
  // white dew turning to frost, a beloved figure across the water,
  // mist, autumn reed straw, and cold current. Glyph label uses the
  // poem title itself.
  "jianjia",
  // 楚辞 · 九歌 — Qu Yuan's southern shamanic songs: Wushan goddess,
  // Yunmeng marsh, cloud banners, orchid/angelica fragrance, jade
  // pendants, bronze bells, cinnabar ritual light, and drifting
  // spirit-mist.
  "jiuge",
  // Vendor-neutral market terminal — dense black-screen information
  // appliance: amber command line, green/red ticks, cyan function
  // keys, scanline grid, tabular numerals. Inspired by financial
  // market terminals without carrying a product name into the code.
  "terminal",
] as const;
export type Palette = (typeof PALETTES)[number];

export const THEMES = ["light", "dark"] as const;
export type Theme = (typeof THEMES)[number];

export const DENSITIES = ["compact", "comfortable"] as const;
export type Density = (typeof DENSITIES)[number];

// Background-pattern toggle. When "on", each palette's wagara (麻の葉
// for shu, 雪輪 for kobai, 七宝 for murasaki, double-headed eagle for
// porphyra, hussar frogging for dekabrist, etc.) renders behind the
// page chrome. When "off", only the base washi grain is shown — the
// palette character stays in the colour tokens + decorations
// (Hanko, focus rings, H1 rules) but the texture goes away for
// operators who find it distracting against dense data UIs.
export const BG_PATTERNS = ["on", "off"] as const;
export type BgPattern = (typeof BG_PATTERNS)[number];

/** Display-name pairs for the palette switcher UI.
 *
 * For the Heian-aesthetic palettes the ``jp`` label is the kasane
 * name itself in kanji; ``en`` is the romanised form a non-reader can
 * pronounce. The Russian palette uses a Cyrillic abbreviation in the
 * ``jp`` slot — the slot name is historical, the field is "native-
 * script glyph". Aesthetic notes live in the comment beside each
 * entry so an operator hovering the picker tooltip has a one-line
 * reference. */
export const PALETTE_LABELS: Record<Palette, { jp: string; en: string }> = {
  shu: { jp: "朱", en: "Shu" },
  ai: { jp: "藍", en: "Ai" },
  sumi: { jp: "墨", en: "Sumi" },
  cha: { jp: "茶", en: "Cha" },
  kin: { jp: "金", en: "Kin" },
  // 紅梅 — red plum on early-spring snow (Genji ch. 32, 梅枝).
  kobai: { jp: "紅梅", en: "Kōbai" },
  // 紫 — Heian violet, the colour Murasaki Shikibu took her name from.
  murasaki: { jp: "紫", en: "Murasaki" },
  // 朽葉 — decayed leaves of late autumn, mono no aware.
  kuchiba: { jp: "朽葉", en: "Kuchiba" },
  // 露草 — dayflower blue at summer dawn.
  tsuyukusa: { jp: "露草", en: "Tsuyukusa" },
  // Декабрист — Decembrist, the December-1825 conspirators.
  // Cyrillic abbreviation matches the visual weight of the 1-2 kanji
  // glyphs in the switcher; the Kanji primitive auto-detects the
  // script and falls back to a Latin/Cyrillic-capable font.
  dekabrist: { jp: "Дек", en: "Dekabrist" },
  // Πορφύρα — Tyrian purple, the imperial dye reserved for the
  // Byzantine basileus. "Porphyrogenitus" — born in the porphyra
  // chamber of the Great Palace. Greek abbreviation Πορ in the
  // glyph slot; the Kanji primitive routes Greek through Plex Sans.
  porphyra: { jp: "Πορ", en: "Porphyra" },
  // Stendhal's "Le Rouge et le Noir" — the title's signature
  // dichotomy carried verbatim into the switcher. Latin letters
  // render in Shippori Mincho (the default JP serif covers Latin
  // perfectly), so no special font detection needed here.
  stendhal: { jp: "R&N", en: "Stendhal" },
  // 宋 — the Song dynasty (960–1279). Single CJK character renders
  // in the JP serif (the kanji 宋 is shared with Japanese — used in
  // 宋朝体, "Song-dynasty typography" — so Shippori Mincho carries
  // the glyph natively).
  song: { jp: "宋", en: "Song" },
  // 蒹葭 — "The reeds and rushes are deeply green; white dew becomes
  // frost." The label uses the poem title; the Latin side uses
  // pinyin without tone marks for compact switcher fit.
  jianjia: { jp: "蒹葭", en: "Jianjia" },
  // 九歌 — Qu Yuan's "Nine Songs" cycle from the Chu Ci. The glyph
  // label is the work title; the palette voice is Wushan goddess /
  // Yunmeng marsh ritual mist rather than court-literati restraint.
  jiuge: { jp: "九歌", en: "Jiuge" },
  // Market terminal. Latin glyph keeps the switcher vendor-neutral
  // while making the black-screen palette obvious at a glance.
  terminal: { jp: "TERM", en: "Terminal" },
};

/** First-load defaults. */
export const DEFAULT_PALETTE: Palette = "shu";
export const DEFAULT_THEME: Theme = "light";
export const DEFAULT_DENSITY: Density = "compact";
export const DEFAULT_BG_PATTERN: BgPattern = "on";

/** Sensitivity tier order (used by the topbar S-key cycle). */
export const SENS_TIERS = ["public", "internal", "confidential", "restricted"] as const;
export type SensTier = (typeof SENS_TIERS)[number];

export const SENS_LABELS: Record<SensTier, { jp: string; en: string }> = {
  public: { jp: "公開", en: "Public" },
  internal: { jp: "社内", en: "Internal" },
  confidential: { jp: "機密", en: "Confidential" },
  restricted: { jp: "厳秘", en: "Restricted" },
};
